import React from "react";
import "./App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "./components/error-boundary/ErrorBoundary";
import LeadCollect from "./pages/lead-collect/LeadCollect";
import LeadConfirm from "./pages/lead-confirm/LeadConfirm";
import ThankYou from "./pages/thank-you/ThankYou";
import { AuthProvider } from "./providers/AuthProvider";
import { ProtectedRoute } from "./components/protected-route/ProtectedRoute";
import Login from "./pages/login/Login";
import LeadList from "./pages/lead-list/LeadList";
import Unauthorized from "./pages/unauthorized/Unauthorized";
import AuthLayout from "./components/layout/auth-layout/AuthLayout";
import NotFound from "./pages/not-found/NotFound";
import ProfiloUtente from "./pages/profilo-utente/ProfiloUtente";
import Dashboard from "./pages/dashboard/Dashboard";
import Smist from "./pages/smist/Smist";
import CreateUser from "./pages/creazione-utente/CreateUser";
import ResetPassword from "./pages/reset-password/ResetPassword";
import RecoveryPassword from "./pages/recovery-password/RecoveryPassword";
import OrganizationDetails from "./pages/fornitore/OrganizationDetails";
import LeadConfirmWhiteLabel from "./pages/lead-confirm-white-label/LeadConfirmWhiteLabel";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { LpStandalone } from "./pages/lp-standalone/LpStandalone";
import { ListaLanding } from "./pages/lista-landing/ListaLanding";
import OpenLayout from "./components/layout/open-layout/OpenLayout";
import LpAgente from "./pages/lp-agente/LpAgente";
import ThankYouLpAgente from "./pages/thank-you-lp-agente/ThankYouLpAgente";
// LP Agenti
import LpAgents from "./pages/lp-agents/LpAgents";
import LpAgentCreate from "./pages/lp-agents/LpAgentCreate";
import LpAgentDetail from "./pages/lp-agents/LpAgentDetail";
// EGG Gateway
import EggRulesList from "./pages/egg-rules/EggRulesList";
import EggRuleCreate from "./pages/egg-rules/EggRuleCreate";
import EggRuleDetail from "./pages/egg-rules/EggRuleDetail";
// EGG Webhook Keys
import WebhookKeysList from "./pages/egg-webhook-keys/WebhookKeysList";
import WebhookKeyCreate from "./pages/egg-webhook-keys/WebhookKeyCreate";
import WebhookKeyDetail from "./pages/egg-webhook-keys/WebhookKeyDetail";
// Lead Dispatching
import RulesList from "./pages/lead-dispatch/RulesList";
import RuleCreate from "./pages/lead-dispatch/RuleCreate";
import RuleDetail from "./pages/lead-dispatch/RuleDetail";
import LeadDispatchLogs from "./pages/lead-dispatch/LeadDispatchLogs";
// Chatbot
import TenantList from "./pages/chatbot-tenants/TenantList";
import TenantCreate from "./pages/chatbot-tenants/TenantCreate";
import TenantDetail from "./pages/chatbot-tenants/TenantDetail";
import ChatbotList from "./pages/chatbot-bots/ChatbotList";
import ChatbotCreate from "./pages/chatbot-bots/ChatbotCreate";
import ChatbotDetail from "./pages/chatbot-bots/ChatbotDetail";
import FlowList from "./pages/chatbot-flows/FlowList";
import FlowBuilder from "./pages/chatbot-flows/FlowBuilder";
import MonitoringDashboard from "./pages/chatbot-monitoring/MonitoringDashboard";
import ConversationList from "./pages/chatbot-monitoring/ConversationList";
import ConversationDetail from "./pages/chatbot-monitoring/ConversationDetail";
import CollectedDataList from "./pages/chatbot-monitoring/CollectedDataList";
import CollectedDataDetail from "./pages/chatbot-monitoring/CollectedDataDetail";

const theme = createTheme({
  palette: {
    primary: {
      // #4f3fff
      main: "#4e3fff",
    },
    secondary: {
      main: "#FF69B4",
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: "Inter, sans-serif",
  },
  components: {
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: "0px 2px 6px 1px rgba(150,150,180,0.1)",
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#4e3fff',
        },
      },
    }
  },
});

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/lp-agente/:hash"
              element={<OpenLayout children={<LpAgente />} />}
            />
            <Route element={<ProtectedRoute />}>
              <Route
                path="/dashboard"
                element={
                  <AuthLayout>
                    <Dashboard />
                  </AuthLayout>
                }
              />
              <Route
                path="/user-profile"
                element={
                  <AuthLayout>
                    <ProfiloUtente />
                  </AuthLayout>
                }
              />
            </Route>
            <Route
              element={
                <ProtectedRoute allowedRoles={["admin", "create_lead"]} />
              }
            >
              <Route
                path="/lead-collect"
                element={
                  <AuthLayout>
                    <LeadCollect />
                  </AuthLayout>
                }
              />
            </Route>
            <Route
              element={
                <ProtectedRoute allowedRoles={["admin", "lp_standalone"]} />
              }
            >
              <Route
                path="/lp-standalone/:hash"
                element={
                  <AuthLayout>
                    <LpStandalone />
                  </AuthLayout>
                }
              />
              <Route
                path="/lp-list"
                element={
                  <AuthLayout>
                    <ListaLanding />
                  </AuthLayout>
                }
              />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "quality_op", "create_lead"]}
                />
              }
            >
              <Route
                path="/lead-list"
                element={
                  <AuthLayout>
                    <LeadList />
                  </AuthLayout>
                }
              />
              <Route />
            </Route>
            <Route
              element={
                <ProtectedRoute allowedRoles={["admin", "fornitore_admin"]} />
              }
            >
              <Route
                path="/organization-details"
                element={
                  <AuthLayout>
                    <OrganizationDetails />
                  </AuthLayout>
                }
              />
              <Route />
            </Route>
            <Route
              element={
                <ProtectedRoute allowedRoles={["admin", "dispatcher_admin"]} />
              }
            >
              <Route
                path="/smist"
                element={
                  <AuthLayout>
                    <Smist />
                  </AuthLayout>
                }
              />
            </Route>
            <Route
              element={
                <ProtectedRoute allowedRoles={["admin", "user_admin"]} />
              }
            >
              <Route
                path="/create-user"
                element={
                  <AuthLayout>
                    <CreateUser />
                  </AuthLayout>
                }
              />
            </Route>
            {/* ── LP Agenti ──────────────────────────────────────── */}
            <Route
              element={
                <ProtectedRoute allowedRoles={["admin", "lp_admin", "lp_builder"]} />
              }
            >
              <Route path="/lp-agents" element={<AuthLayout><LpAgents /></AuthLayout>} />
              <Route path="/lp-agents/create" element={<AuthLayout><LpAgentCreate /></AuthLayout>} />
              <Route path="/lp-agents/:id" element={<AuthLayout><LpAgentDetail /></AuthLayout>} />
            </Route>
            {/* ── Chatbot ────────────────────────────────────────── */}
            <Route
              element={
                <ProtectedRoute allowedRoles={["admin", "chatbot_admin"]} />
              }
            >
              <Route
                path="/chatbot"
                element={<Navigate to="/chatbot/tenants" replace />}
              />
              {/* Tenants */}
              <Route
                path="/chatbot/tenants"
                element={<AuthLayout><TenantList /></AuthLayout>}
              />
              <Route
                path="/chatbot/tenants/new"
                element={<AuthLayout><TenantCreate /></AuthLayout>}
              />
              <Route
                path="/chatbot/tenants/:tenantId"
                element={<AuthLayout><TenantDetail /></AuthLayout>}
              />
              {/* Chatbots */}
              <Route
                path="/chatbot/chatbots"
                element={<AuthLayout><ChatbotList /></AuthLayout>}
              />
              <Route
                path="/chatbot/chatbots/new"
                element={<AuthLayout><ChatbotCreate /></AuthLayout>}
              />
              <Route
                path="/chatbot/chatbots/:chatbotId"
                element={<AuthLayout><ChatbotDetail /></AuthLayout>}
              />
              {/* Flows */}
              <Route
                path="/chatbot/flows"
                element={<AuthLayout><FlowList /></AuthLayout>}
              />
              <Route
                path="/chatbot/flows/:flowId"
                element={<AuthLayout><FlowBuilder /></AuthLayout>}
              />
              {/* Monitoring */}
              <Route
                path="/chatbot/monitoring"
                element={<AuthLayout><MonitoringDashboard /></AuthLayout>}
              />
              <Route
                path="/chatbot/monitoring/conversations"
                element={<AuthLayout><ConversationList /></AuthLayout>}
              />
              <Route
                path="/chatbot/monitoring/conversations/:conversationId"
                element={<AuthLayout><ConversationDetail /></AuthLayout>}
              />
              <Route
                path="/chatbot/monitoring/collected"
                element={<AuthLayout><CollectedDataList /></AuthLayout>}
              />
              <Route
                path="/chatbot/monitoring/collected/:recordId"
                element={<AuthLayout><CollectedDataDetail /></AuthLayout>}
              />
            </Route>
            {/* EGG Gateway */}
            <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
              <Route
                path="/egg/rules"
                element={<AuthLayout><EggRulesList /></AuthLayout>}
              />
              <Route
                path="/egg/rules/new"
                element={<AuthLayout><EggRuleCreate /></AuthLayout>}
              />
              <Route
                path="/egg/rules/:ruleId"
                element={<AuthLayout><EggRuleDetail /></AuthLayout>}
              />
              <Route
                path="/egg/webhook-keys"
                element={<AuthLayout><WebhookKeysList /></AuthLayout>}
              />
              <Route
                path="/egg/webhook-keys/new"
                element={<AuthLayout><WebhookKeyCreate /></AuthLayout>}
              />
              <Route
                path="/egg/webhook-keys/:keyId"
                element={<AuthLayout><WebhookKeyDetail /></AuthLayout>}
              />
            </Route>
            {/* Lead Dispatching — Rules: admin OR dispatcher_admin */}
            <Route
              element={<ProtectedRoute allowedRoles={["admin", "dispatcher_admin"]} />}
            >
              <Route
                path="/lead-dispatch/rules"
                element={<AuthLayout><RulesList /></AuthLayout>}
              />
              <Route
                path="/lead-dispatch/rules/new"
                element={<AuthLayout><RuleCreate /></AuthLayout>}
              />
              <Route
                path="/lead-dispatch/rules/:ruleId"
                element={<AuthLayout><RuleDetail /></AuthLayout>}
              />
            </Route>
            {/* Lead Dispatching — Log globali: admin only */}
            <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
              <Route
                path="/lead-dispatch/logs"
                element={<AuthLayout><LeadDispatchLogs /></AuthLayout>}
              />
            </Route>
            {/* ──────────────────────────────────────────────────── */}
            <Route path="/" element={<Login />}></Route>
            <Route path="/thank-you" element={<ThankYou />}></Route>
            <Route path="/thank-you-lp-agente" element={<ThankYouLpAgente />} />
            <Route path="/reset-password" element={<ResetPassword />}></Route>
            <Route
              path="/recovery-password/:hash1/:hash2"
              element={<RecoveryPassword />}
            ></Route>
            <Route path="/lead-confirm" element={<LeadConfirm />}></Route>
            <Route
              path="/lead-confirm-wl"
              element={<LeadConfirmWhiteLabel />}
            ></Route>
            <Route path="/unauthorized" element={<Unauthorized />}></Route>
            <Route path="*" element={<NotFound />}></Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
